import { PayloadAction, ThunkAction, createSlice } from "@reduxjs/toolkit";
import { generateReportLatestIdKey } from "@sscs/report/utils";
import { REPORT_TYPE_REPRO } from "Constants";
import { sdlcReportApi } from "Services";
import { ReportFileSummary } from "Types";
import {
  generateReportKey,
  generateReportReturnToKey,
  getReportKeyFromPath,
  markReportAsDeleted,
} from "Utils";
import { RootState } from "./rootReducer";
import { ReportIdentifiers, belongsToProjectPackageOrVersion } from "./utils";

export type ReportsSliceState = {
  reports: Record<string, ReportFileSummary>;
  deletedReports: string[];
};

const initialState: ReportsSliceState = {
  reports: {},
  deletedReports: [],
};

export const reportsSlice = createSlice({
  name: "reports",
  initialState,
  reducers: {
    updateReport: (
      state,
      action: PayloadAction<{
        reportId: string;
        report: Partial<ReportFileSummary>;
      }>,
    ) => {
      if (state.reports[action.payload.reportId]) {
        state.reports[action.payload.reportId] = {
          ...state.reports[action.payload.reportId],
          ...action.payload.report,
        };
      }
    },
    removeReports: (state, action: PayloadAction<string[]>) => {
      action.payload.forEach((key) => delete state.reports[key]);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(markReportAsDeleted, (state, action) => {
      state.deletedReports.push(action.payload);
    });
    builder.addMatcher(
      sdlcReportApi.endpoints.getLatestReport.matchFulfilled,
      (state, action) => {
        const args = action.meta.arg.originalArgs;
        const reportKey = generateReportKey(args);
        const isRepro = args.build
          ? args.build === REPORT_TYPE_REPRO
          : undefined;
        let tabLabel = action.payload.file_info.file_name;

        if (action.payload.file_info.file_type === "version") {
          tabLabel = `${action.payload.file_info.product} ${action.payload.file_info.version}`;
        }

        if (isRepro) {
          tabLabel = `Repro for: ${tabLabel}`;
        }

        state.reports[reportKey] = {
          tabLabel: tabLabel,
          groupId: args.groupId,
          projectName: args.projectName,
          packageName: args.packageName,
          version: args.version,
          isRepro: isRepro,
          diffWith: args.diffWith,
        };
      },
    );
  },
});

export default reportsSlice.reducer;

const { removeReports } = reportsSlice.actions;

export const { updateReport } = reportsSlice.actions;

export const removeReportsThunk =
  (
    reportKeys: string[],
  ): ThunkAction<void, RootState, unknown, PayloadAction<unknown>> =>
  (dispatch, getState) => {
    reportKeys.forEach((closedReportKey) => {
      const closedReportReturnTo = sessionStorage.getItem(
        generateReportReturnToKey(closedReportKey),
      );

      Object.keys(getState().reports.reports).forEach((reportKey) => {
        const returnToKey = generateReportReturnToKey(reportKey);
        const returnToRawData = sessionStorage.getItem(returnToKey);

        if (!returnToRawData) return;

        if (
          getReportKeyFromPath(JSON.parse(returnToRawData).from) !==
          closedReportKey
        )
          return;

        if (
          closedReportReturnTo !== null &&
          getReportKeyFromPath(JSON.parse(closedReportReturnTo).from) !==
            reportKey
        ) {
          sessionStorage.setItem(returnToKey, closedReportReturnTo);
        } else {
          sessionStorage.removeItem(returnToKey);
        }
      });

      sessionStorage.removeItem(closedReportKey);
      sessionStorage.removeItem(generateReportLatestIdKey(closedReportKey));
      sessionStorage.removeItem(generateReportReturnToKey(closedReportKey));
    });

    dispatch(removeReports(reportKeys));
  };

export const removeAllReportsThunk =
  (
    reportDetails: ReportIdentifiers,
  ): ThunkAction<void, RootState, unknown, PayloadAction<unknown>> =>
  (dispatch, getState) => {
    const state = getState();

    // When file or version are deleted, we need to clear all associated reports
    // These include diffs, repro builds, etc
    const matchingReports = Object.keys(state.reports.reports).filter(
      (reportKey) => belongsToProjectPackageOrVersion(reportKey, reportDetails),
    );

    dispatch(removeReportsThunk(matchingReports));
  };

export const selectReports = (state: RootState): ReportsSliceState =>
  state.reports;
